import React, { useEffect } from 'react';

// Serialise JSON-LD safely. Product names/descriptions are user-controlled, so
// escape the characters that could break out of the <script> tag (chiefly
// "</script>"). A JSON-LD block is read as data, not executed as JS, so escaping
// < > & is sufficient — the result is still valid JSON-LD.
const safeJsonLd = (data) =>
  JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');

// Centralised site identity for SEO. If you move to a custom domain, change
// SITE_URL here (and in public/index.html, robots.txt, sitemap.xml).
export const SITE_URL = 'https://bikiniworldkenya.co.ke';
export const SITE_NAME = 'Bikini World';
const DEFAULT_TITLE = 'Bikini World — Swimwear & Beachwear';
const DEFAULT_DESCRIPTION =
  'Shop swimwear, bikinis and beachwear at Bikini World — quality pieces for sun, ' +
  'surf and endless summer. Fast delivery across Kenya, free shipping over KSh 12,000.';
const DEFAULT_IMAGE = `${SITE_URL}/logo.jpeg`;

// Finds an existing <meta> by its name/property attribute and updates its
// content in place, creating one only if none exists yet.
//
// This can't be done declaratively via JSX. public/index.html carries a
// static baseline set of these same tags — needed because Firebase Hosting
// serves that file directly for "/", bypassing our SSR/prerender function
// (see functions/index.js: getShellHtml fetches /index.html as a real static
// file on purpose, to bootstrap prerendering for every other route without
// recursion). React 19's built-in <head> tag deduping only keys off
// name/httpEquiv/itemProp/charSet — NOT `property` — so a page's own
// <meta property="og:title"> never replaced the static one; both ended up in
// the DOM, and crawlers (WhatsApp, etc.) saw two conflicting og:image values.
// Upserting by hand guarantees exactly one tag per key, on every route,
// whether the existing one came from the static shell or a previous page.
function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr, key) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Per-page SEO. Sets <title>/<meta>/<link> imperatively (see upsertMeta above)
// so each page's tags cleanly replace the previous page's — or the static
// defaults in public/index.html — instead of piling up duplicates.
const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  path = '',
  type = 'website',
  noIndex = false,
  jsonLd,
}) => {
  // Don't double-brand titles that already name the site (e.g. a blog post's
  // seoTitle "… | Bikini World").
  const fullTitle = !title
    ? DEFAULT_TITLE
    : title.toLowerCase().includes(SITE_NAME.toLowerCase()) ? title : `${title} · ${SITE_NAME}`;
  const url = `${SITE_URL}${path}`;
  const blocks = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : (jsonLd ? [jsonLd] : []);

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta('name', 'description', description);
    upsertLink('canonical', url);
    if (noIndex) upsertMeta('name', 'robots', 'noindex,nofollow');
    else removeMeta('name', 'robots');

    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:locale', 'en_KE');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);

    // Signal for the prerenderer (functions/index.js) — it waits on this flag
    // instead of guessing readiness from rendered text length, so it never
    // captures a page before the real og:image/title have been set. Pages
    // like ProductDetail only mount <Seo> once their data has fully loaded
    // (the loading/error states render no <Seo> at all), so by the time this
    // runs at least once, the tags above already reflect final data.
    window.__seoReady = true;
  }, [fullTitle, description, image, url, type, noIndex]);

  return (
    <>
      {/* Structured data (JSON-LD blocks are additive by design — a product's
          schema doesn't conflict with the site-wide LocalBusiness ones — so
          these stay declarative rather than upserted). */}
      {blocks.map((data, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(data) }}
        />
      ))}
    </>
  );
};

export default Seo;
